import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // Import the UTC plugin
import timezone from "dayjs/plugin/timezone"; // Import the timezone plugin
import customParseFormat from "dayjs/plugin/customParseFormat"; // For parsing custom formats
dayjs.extend(timezone); // Extend dayjs with the timezone plugin

dayjs.extend(utc); // Extend dayjs with the UTC plugin
dayjs.extend(customParseFormat); // Extend dayjs with the custom parse format plugin

export function formatDateFromString(dateString: string): string {
  // Check if the input is a valid date string
  if (!dateString || dateString.trim() === '') {
    return '-';
  }

  const date = dayjs.utc(dateString).local();

  // Check if the parsed date is valid
  if (!date.isValid()) {
    return '-';
  }

  // Get the day of the month
  const day = date.date();
  // Format the day with the appropriate suffix
  const dayWithSuffix = day + getDaySuffix(day);

  return `${dayWithSuffix} ${date.format("MMMM YYYY")}`;
}

export function formatDateAndTimeFromString(dateString: string): string {
  if (dateString === undefined || dateString === "") {
    return "-"
  }
  const date = dayjs.utc(dateString).local(); // Parse the date as UTC and convert to local time

  // Format the date and time using the desired format string
  const formattedDateTime = date.format("D MMM YYYY, h:mm A");

  return formattedDateTime;
}

export function separateDocsByComma(input: string): string[] {
  if (!input) {
    return [];
  }

  const items = input.split(",");
  return items.map((item) => item.trim().replace(/_/g, " ")).filter(Boolean);
}

// Helper function to get the day suffix (st, nd, rd, th)
function getDaySuffix(day: number): string {
  if (day > 3 && day < 21) return "th"; // 11th, 12th, 13th etc.
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}
export function getUrlPathId(url: string) {
  const parts = url.split("/");
  return parts[parts.length - 1];
}

export function getUrlPathTextId(url: string) {
  const parts = url.split("/");
  return parts[parts.length - 2];
}

export const formatDate = (date: string) => {
  return dayjs(date).format("D MMMM YYYY");
};

export function formatJobType(input: string): string {
  if (input.includes("_")) {
    // If the string contains underscores
    return input
      .split("_") // Split by underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
      .join(" "); // Join with spaces
  } else {
    // If the string does not contain underscores
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase(); // Capitalize first letter only
  }
}

export const formatTime = (dateString: string) => {
  return dayjs(dateString).format("hh:mm:ss A");
};
export function formatTextType(input: string): string {
  if (input === undefined || input === null) {
    return "-";
  }
  if (input?.includes("_")) {
    // If the string contains underscores
    return input
      .split("_") // Split by underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
      .join(" "); // Join with spaces
  } else {
    // If the string does not contain underscores
    return input?.charAt(0).toUpperCase() + input.slice(1).toLowerCase(); // Capitalize first letter only
  }
}

export function limitToSeven(str: string) {
  return str.substring(0, 7);
}

export function formatFullName(firstName: string, lastName: string): string {
  if (!firstName || !lastName) {
    return "";
  }

  const firstInitial = firstName[0]?.toUpperCase() ?? "";
  const restOfFirstName = firstName.slice(1).toLowerCase();
  const lastInitial = lastName[0]?.toUpperCase() ?? "";
  const restOfLastName = lastName.slice(1).toLowerCase();

  return `${firstInitial}${restOfFirstName} ${lastInitial}${restOfLastName}`;
}

export const formatDecimal = (value: string) => {
  if (!value) {
    return "-";
  }
  return parseFloat(value).toFixed(1);
};

export const formatDecimalNumber = (value: number) => {
  if (value === 0) {
    return 0;
  }

  if (isNaN(value) || !isFinite(value)) {
    return "-";
  }

  return parseFloat(`${value}`).toFixed(1);
};

export function extractContractDates(input: string): string | null {
  if (!input) {
    return "-";
  }
  const regex =
    /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z) - (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z)/;
  const match = input.match(regex);

  if (!match) {
    return null;
  }

  const [startDate, endDate] = match.slice(1);

  const formattedStartDate = dayjs(startDate).format("D MMMM YYYY");
  const formattedEndDate = dayjs(endDate).format("D MMMM YYYY");

  return `${formattedStartDate} - ${formattedEndDate}`;
}
